import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Compass, LogOut, Inbox, ClipboardCheck, Newspaper, MessageSquareQuote,
  BarChart3, Trash2, Plus, ArrowLeft, Pencil,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { BLOG_CATEGORIES } from "../data/content";
import { usePageMeta } from "../lib/motion";
import ImageUpload from "../components/ImageUpload";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-gold/60 transition-colors";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-deep flex items-center justify-center px-4" data-testid="admin-login">
      <div className="w-full max-w-md glass-card rounded-2xl p-8 sm:p-10">
        <div className="flex items-center gap-3 mb-8">
          <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 bg-gold/10">
            <Compass className="h-5 w-5 text-gold" />
          </span>
          <div>
            <p className="font-display font-extrabold text-white leading-none">THE TRIP <span className="text-gold">WORLD</span></p>
            <p className="text-[11px] tracking-[0.25em] text-slate-500 font-display mt-1">ADMIN PANEL</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin Email" data-testid="admin-email-input" className={inputCls} />
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="admin-password-input" className={inputCls} />
          {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-submit-btn" className="btn-gold w-full rounded-full py-3.5 text-sm font-bold tracking-wider font-display disabled:opacity-60">
            {loading ? "SIGNING IN..." : "SIGN IN"}
          </button>
        </form>
      </div>
    </div>
  );
};

const Empty = ({ label }) => (
  <div className="rounded-xl border border-dashed border-white/15 p-10 text-center text-sm text-slate-500" data-testid="admin-empty">
    {label}
  </div>
);

const EnquiriesTab = () => {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/enquiries").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    await api.delete(`/admin/enquiries/${id}`);
    toast.success("Enquiry deleted");
    load();
  };
  if (!items) return null;
  if (items.length === 0) return <Empty label="No enquiries yet." />;
  return (
    <div className="space-y-4" data-testid="admin-enquiries-list">
      {items.map((e) => (
        <div key={e.id} className="glass-card rounded-xl p-5 flex flex-col sm:flex-row sm:items-start gap-4" data-testid={`admin-enquiry-${e.id}`}>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3 mb-1.5">
              <p className="font-display font-bold text-white">{e.name}</p>
              <span className="text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full bg-gold/10 border border-gold/30 text-gold uppercase">
                {e.source}
              </span>
              {e.visa_type && <span className="text-xs text-slate-400">{e.visa_type}</span>}
              {e.country && <span className="text-xs text-slate-400">→ {e.country}</span>}
            </div>
            <p className="text-xs text-slate-400">{e.phone} · {e.email}{e.city ? ` · ${e.city}` : ""}</p>
            {e.message && <p className="text-sm text-slate-300 mt-2">{e.message}</p>}
            <p className="text-[11px] text-slate-600 mt-2">{new Date(e.created_at).toLocaleString("en-IN")}</p>
          </div>
          <button onClick={() => del(e.id)} data-testid={`admin-enquiry-delete-${e.id}`} className="text-slate-500 hover:text-red-400 transition-colors p-2" aria-label="Delete enquiry">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
};

const EligibilityTab = () => {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/eligibility").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    await api.delete(`/admin/eligibility/${id}`);
    toast.success("Request deleted");
    load();
  };
  if (!items) return null;
  if (items.length === 0) return <Empty label="No eligibility requests yet." />;
  return (
    <div className="space-y-4" data-testid="admin-eligibility-list">
      {items.map((e) => (
        <div key={e.id} className="glass-card rounded-xl p-5 flex items-start gap-4" data-testid={`admin-eligibility-${e.id}`}>
          <div className="flex-1">
            <p className="font-display font-bold text-white mb-1.5">{e.name}, {e.age}</p>
            <p className="text-xs text-slate-400">{e.phone} · {e.email}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {[e.visa_type, e.country, e.education, e.occupation, e.experience, e.english_status].filter(Boolean).map((tag) => (
                <span key={tag} className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300">{tag}</span>
              ))}
            </div>
            {e.message && <p className="text-sm text-slate-300 mt-3">{e.message}</p>}
            <p className="text-[11px] text-slate-600 mt-2">{new Date(e.created_at).toLocaleString("en-IN")}</p>
          </div>
          <button onClick={() => del(e.id)} data-testid={`admin-eligibility-delete-${e.id}`} className="text-slate-500 hover:text-red-400 transition-colors p-2" aria-label="Delete request">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
};

const BLANK_POST = { title: "", category: BLOG_CATEGORIES[0], excerpt: "", content: "", cover_image: "", published: true };

const BlogTab = () => {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/posts").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (form.id) await api.put(`/admin/posts/${form.id}`, form);
      else await api.post("/admin/posts", form);
      toast.success(form.id ? "Article updated" : "Article published");
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/admin/posts/${id}`);
    toast.success("Article deleted");
    load();
  };

  if (form)
    return (
      <form onSubmit={save} className="glass-card rounded-xl p-6 space-y-4" data-testid="admin-post-form">
        <h3 className="font-display font-bold text-white">{form.id ? "Edit Article" : "New Article"}</h3>
        <input required minLength={3} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" data-testid="admin-post-title" className={inputCls} />
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-post-category" className={`${inputCls} appearance-none`}>
          {BLOG_CATEGORIES.map((c) => <option key={c} value={c} className="bg-navy">{c}</option>)}
        </select>
        <div>
          <label className="block text-xs font-bold tracking-[0.2em] text-slate-400 font-display uppercase mb-2">Cover Image</label>
          <ImageUpload value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} testid="admin-post-cover" placeholder="Cover image URL or upload (optional)" />
        </div>
        <textarea required value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Excerpt (1-2 sentences)" rows={2} data-testid="admin-post-excerpt" className={`${inputCls} resize-none`} />
        <textarea required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Article content (blank line between paragraphs)" rows={10} data-testid="admin-post-content" className={`${inputCls} resize-y`} />
        <label className="flex items-center gap-2.5 text-sm text-slate-300">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="admin-post-published" className="accent-[#d4af37] h-4 w-4" />
          Published (visible on website)
        </label>
        <div className="flex gap-3">
          <button type="submit" disabled={saving} data-testid="admin-post-save-btn" className="btn-gold rounded-full px-7 py-3 text-xs font-bold tracking-wider font-display disabled:opacity-60">
            {saving ? "SAVING..." : "SAVE ARTICLE"}
          </button>
          <button type="button" onClick={() => setForm(null)} data-testid="admin-post-cancel-btn" className="btn-outline-light rounded-full px-7 py-3 text-xs font-bold tracking-wider font-display">
            CANCEL
          </button>
        </div>
      </form>
    );

  return (
    <div data-testid="admin-blog-list">
      <button onClick={() => setForm(BLANK_POST)} data-testid="admin-post-new-btn" className="mb-5 btn-gold rounded-full px-6 py-3 text-xs font-bold tracking-wider font-display inline-flex items-center gap-2">
        <Plus className="h-4 w-4" /> NEW ARTICLE
      </button>
      {!items ? null : items.length === 0 ? <Empty label="No articles yet." /> : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className="glass-card rounded-xl p-4 flex items-center gap-4" data-testid={`admin-post-${p.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-white truncate">{p.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {p.category} · {p.published ? <span className="text-emerald-400">Published</span> : <span className="text-amber-400">Draft</span>}
                </p>
              </div>
              <button onClick={() => setForm({ ...BLANK_POST, ...p })} data-testid={`admin-post-edit-${p.id}`} className="text-slate-400 hover:text-gold transition-colors p-2" aria-label="Edit article">
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => del(p.id)} data-testid={`admin-post-delete-${p.id}`} className="text-slate-500 hover:text-red-400 transition-colors p-2" aria-label="Delete article">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const BLANK_STORY = { name: "", destination: "", visa_category: "", review: "", photo: "", published: true };

const StoriesTab = () => {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/testimonials", form);
      toast.success("Success story published");
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Story deleted");
    load();
  };

  if (form)
    return (
      <form onSubmit={save} className="glass-card rounded-xl p-6 space-y-4" data-testid="admin-story-form">
        <h3 className="font-display font-bold text-white">New Success Story</h3>
        <p className="text-xs text-slate-500">Publish only genuine client experiences, with the client's consent.</p>
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Client Name" data-testid="admin-story-name" className={inputCls} />
        <div className="grid grid-cols-2 gap-4">
          <input required value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder="Destination (e.g. Canada)" data-testid="admin-story-destination" className={inputCls} />
          <input required value={form.visa_category} onChange={(e) => setForm({ ...form, visa_category: e.target.value })} placeholder="Visa Category" data-testid="admin-story-category" className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-bold tracking-[0.2em] text-slate-400 font-display uppercase mb-2">Client Photo</label>
          <ImageUpload value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} testid="admin-story-photo" placeholder="Client photo URL or upload (optional)" />
        </div>
        <textarea required value={form.review} onChange={(e) => setForm({ ...form, review: e.target.value })} placeholder="Client review" rows={4} data-testid="admin-story-review" className={`${inputCls} resize-none`} />
        <div className="flex gap-3">
          <button type="submit" disabled={saving} data-testid="admin-story-save-btn" className="btn-gold rounded-full px-7 py-3 text-xs font-bold tracking-wider font-display disabled:opacity-60">
            {saving ? "SAVING..." : "PUBLISH STORY"}
          </button>
          <button type="button" onClick={() => setForm(null)} data-testid="admin-story-cancel-btn" className="btn-outline-light rounded-full px-7 py-3 text-xs font-bold tracking-wider font-display">
            CANCEL
          </button>
        </div>
      </form>
    );

  return (
    <div data-testid="admin-stories-list">
      <button onClick={() => setForm(BLANK_STORY)} data-testid="admin-story-new-btn" className="mb-5 btn-gold rounded-full px-6 py-3 text-xs font-bold tracking-wider font-display inline-flex items-center gap-2">
        <Plus className="h-4 w-4" /> NEW SUCCESS STORY
      </button>
      {!items ? null : items.length === 0 ? <Empty label="No success stories yet. The website shows a placeholder until genuine stories are added." /> : (
        <div className="space-y-3">
          {items.map((t) => (
            <div key={t.id} className="glass-card rounded-xl p-4 flex items-center gap-4" data-testid={`admin-story-${t.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-white">{t.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{t.destination} · {t.visa_category}</p>
              </div>
              <button onClick={() => del(t.id)} data-testid={`admin-story-delete-${t.id}`} className="text-slate-500 hover:text-red-400 transition-colors p-2" aria-label="Delete story">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StatsTab = () => {
  const [form, setForm] = useState({ clients: "", applications: "", destinations: "", years: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/stats").then((r) => {
      setForm({
        clients: r.data.clients ?? "",
        applications: r.data.applications ?? "",
        destinations: r.data.destinations ?? "",
        years: r.data.years ?? "",
      });
    }).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : Number(v)])
      );
      await api.put("/admin/stats", payload);
      toast.success("Statistics updated on the website");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const FIELDS = [
    ["clients", "Clients Assisted"],
    ["applications", "Applications Supported"],
    ["destinations", "Destinations"],
    ["years", "Years of Experience"],
  ];

  return (
    <form onSubmit={save} className="glass-card rounded-xl p-6 space-y-5 max-w-xl" data-testid="admin-stats-form">
      <p className="text-xs text-slate-500 leading-relaxed">
        Set only real, verifiable numbers. Leave a field empty to hide it from the website — the statistics
        section appears only when at least one value is set.
      </p>
      {FIELDS.map(([key, label]) => (
        <div key={key}>
          <label className="block text-xs font-bold tracking-[0.2em] text-slate-400 font-display uppercase mb-2">{label}</label>
          <input
            type="number"
            min="0"
            value={form[key]}
            onChange={(e) => setForm({ ...form, [key]: e.target.value })}
            placeholder="Not set"
            data-testid={`admin-stats-${key}`}
            className={inputCls}
          />
        </div>
      ))}
      <button type="submit" disabled={saving} data-testid="admin-stats-save-btn" className="btn-gold rounded-full px-7 py-3 text-xs font-bold tracking-wider font-display disabled:opacity-60">
        {saving ? "SAVING..." : "SAVE STATISTICS"}
      </button>
    </form>
  );
};

const TABS = [
  { key: "enquiries", label: "Enquiries", icon: Inbox, component: EnquiriesTab },
  { key: "eligibility", label: "Eligibility", icon: ClipboardCheck, component: EligibilityTab },
  { key: "blog", label: "Blog", icon: Newspaper, component: BlogTab },
  { key: "stories", label: "Success Stories", icon: MessageSquareQuote, component: StoriesTab },
  { key: "stats", label: "Statistics", icon: BarChart3, component: StatsTab },
];

const AdminPage = () => {
  usePageMeta("Admin", "The Trip World admin panel.");
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("enquiries");

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
    toast.success("Signed out");
  };

  if (user === null) return <div className="min-h-screen bg-navy-deep" />;
  if (user === false) return <Login onLogin={setUser} />;

  const Active = TABS.find((t) => t.key === tab).component;

  return (
    <div className="min-h-screen bg-navy-deep" data-testid="admin-dashboard-panel">
      <header className="glass-dark border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/40 bg-gold/10">
              <Compass className="h-4 w-4 text-gold" />
            </span>
            <p className="font-display font-extrabold text-white text-sm">
              THE TRIP <span className="text-gold">WORLD</span>
              <span className="block text-[10px] font-medium tracking-[0.25em] text-slate-500">ADMIN PANEL</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} data-testid="admin-view-site-btn" className="btn-outline-light rounded-full px-5 py-2 text-xs font-bold tracking-wider font-display inline-flex items-center gap-2">
              <ArrowLeft className="h-3.5 w-3.5" /> VIEW SITE
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="rounded-full px-5 py-2 text-xs font-bold tracking-wider font-display bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors inline-flex items-center gap-2">
              <LogOut className="h-3.5 w-3.5" /> LOGOUT
            </button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`rounded-full px-5 py-2.5 text-xs font-bold tracking-wider font-display inline-flex items-center gap-2 transition-all ${
                tab === t.key ? "btn-gold" : "btn-outline-light"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" /> {t.label.toUpperCase()}
            </button>
          ))}
        </div>
        <Active />
      </div>
    </div>
  );
};

export default AdminPage;
